/**
 * 긴 텍스트 접기(InputWidget.collapseLines) — 표시 상태의 값을 N줄까지만 보이고 나머지는
 * "펼치기" 토글 뒤로 접는다. **순수 길이 처리**다: 값 내용을 파싱해 제목/요약을 추론하지 않고
 * (첫 줄이 무엇인지 코드는 모른다) 줄 수 N도 저작자가 정한다.
 *
 * 규약(shared JSDoc 정본):
 * - 펼침/접힘은 **저장하지 않는** 렌더러 로컬 state(그룹 위젯 collapsible 동형).
 * - **본문·상세·공유·PDF(print)는 항상 전문** — 이 모듈은 작성폼 렌더(renderWriteNode /
 *   TextareaListField)만 소비하고 본문 렌더 경로(RenderTemplate + body binding)는 import하지
 *   않는다. print 분기 판정이 아니라 **소비처 분리**로 보장한다(법적 증빙이 접힌 채 인쇄 금지).
 * - 접기는 값이 **텍스트로 표시될 때만** — readonly 값 표시, editEntry:'click'의 표시 상태
 *   (목록형은 항목 단위). 입력 컨트롤(textarea·input)엔 미적용 = 편집 진입이 곧 전문(자동 펼침).
 *
 * 토글 표현은 **구 안전점검보고서(LegalBasisChip)의 chevron 결**을 따른다 — 접힘 ▶ / 펼침 ▼
 * 아이콘 버튼이고 "펼치기"/"접기" 문구는 화면에 쓰지 않는다(같은 동작이 두 표현을 갖지 않도록
 * 전역 통일). 동작 문구는 aria-label/title로만 남는다.
 *
 * rc 신규 Tailwind 유틸 금지 규약(memory: rc-package-tailwind-class-purge-inline-style)에 따라
 * 임계 스타일(줄 클램프·토글 버튼·chevron 치수)은 전부 인라인 style이다 — line-clamp 유틸을 쓰면
 * 앱 CSS에 생성되지 않아 "클래스는 붙는데 안 접히는" 무스타일이 된다(구 SI의 `w-3 h-3`도 클래스로
 * 옮겨 오면 같은 이유로 크기 없는 svg가 된다).
 */
import { useState, type CSSProperties, type ReactElement } from 'react';

/**
 * 줄바꿈 없는 긴 문단도 접기 대상으로 인정하기 위한 한 줄 문자 수 어림(보수적).
 * 폭을 모르는 정적 판정이라 정확할 수 없고, 정확할 필요도 없다 — 이 값은 **토글을 띄울지**만
 * 정하고(띄우면 전문 접근은 항상 가능), 실제 잘림은 CSS 클램프가 한다. 과대 추정(토글이 떠도
 * 접히는 줄이 안 보임)은 무해하지만 과소 추정은 토글 없는 잘림을 만들 수 있어, 접힘 클램프는
 * collapsible일 때만 적용한다.
 */
const APPROX_CHARS_PER_LINE = 40;

export interface TextCollapseState {
  /** 접기 대상인가 — false면 호출부는 전문을 그대로(토글 없이) 렌더한다. */
  collapsible: boolean;
  /** 접힌 상태에서 보여 줄 텍스트 — 줄바꿈 기준 앞 N줄(비대상이면 원문 그대로). */
  head: string;
  /**
   * head 뒤에 남는 줄 — 펼쳤을 때 **아래 블록**으로 붙는다(구 안전점검보고서 결: 제목 행 +
   * 본문 블록). 줄바꿈이 없어 나눌 수 없는 문단 접힘이면 빈 문자열이고, 그때는 호출부가
   * 제자리에서 전문을 편다.
   */
  rest: string;
  /** 줄바꿈 기준 감춰지는 줄 수 — 0이면 줄 수 안내 없이 '펼치기'만(문단 wrap 추정 접힘). */
  hiddenLines: number;
  /** 시각 클램프 줄 수(=유효 N). 비대상이면 0. */
  visibleLines: number;
}

/**
 * 접기 판정 — 상호작용 없는 순수 함수(rc에 jsdom이 없어 상태 전이는 테스트 불가라 판정만 분리).
 * 대상: 줄바꿈 수가 N을 넘거나(확정), 줄바꿈이 없어도 N줄에 담기 어려운 길이(어림).
 */
export function resolveTextCollapse(
  text: string,
  collapseLines: number | undefined,
): TextCollapseState {
  const none: TextCollapseState = {
    collapsible: false,
    head: text,
    rest: '',
    hiddenLines: 0,
    visibleLines: 0,
  };
  const n = Math.floor(collapseLines ?? 0);
  if (!Number.isFinite(n) || n < 1) return none;
  if (text.trim() === '') return none;
  const lines = text.split('\n');
  const hiddenLines = Math.max(0, lines.length - n);
  const longerThanClamp = text.length > n * APPROX_CHARS_PER_LINE;
  if (hiddenLines === 0 && !longerThanClamp) return none;
  return {
    collapsible: true,
    head: lines.slice(0, n).join('\n'),
    rest: hiddenLines > 0 ? lines.slice(n).join('\n') : '',
    hiddenLines,
    visibleLines: n,
  };
}

/**
 * 접힘 토글의 **접근성 라벨**(aria-label/title) — 감춰지는 줄 수를 알 때만 병기(어림 접힘은 줄 수
 * 없이). 화면에는 chevron만 나오므로 스크린리더·툴팁이 동작("펼치기"/"접기")을 얻는 유일한 경로다.
 */
export function collapseToggleLabel(state: TextCollapseState, expanded: boolean): string {
  if (expanded) return '접기';
  return state.hiddenLines > 0 ? `펼치기 (+${state.hiddenLines}줄)` : '펼치기';
}

const collapsedTextStyle = (lines: number): CSSProperties => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

// display:block — 아래 래퍼가 <span>(아무 곳에나 놓여도 유효한 중첩)이라, 접힘 상태의
// `-webkit-box`와 같은 블록 흐름을 명시해야 펼침/접힘 사이 레이아웃이 같아진다.
const EXPANDED_TEXT_STYLE: CSSProperties = {
  display: 'block',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

/**
 * 토글 버튼 — chevron + (접힘일 때) 감춘 줄 수를 한 버튼 안에 담는다(구 SI 동형: 아이콘과 라벨이
 * 하나의 버튼이라 클릭 영역이 넓다). 링크 결(brand-600). 인라인 style(위 purge 규약).
 */
const TOGGLE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  flexShrink: 0,
  // 첫 글자 줄 한가운데에 맞추는 보정 — 아이콘 12px, 본문 한 줄 ≈18px(13px×1.4)이라 (18-12)/2≈3.
  // 구 안전점검보고서도 같은 이유로 chevron에 `mt-0.5`를 준다(정렬 보정이지 여백이 아니다).
  marginTop: 3,
  padding: 0,
  border: 'none',
  background: 'none',
  color: '#4f46e5', // brand-600
  fontSize: 12,
  lineHeight: 1.4,
  cursor: 'pointer',
};

/**
 * 바깥 래퍼 — 제목 행과 본문 블록을 세로로 담는다. `inline-block`인 이유는 이 컴포넌트가 표 셀
 * <span> 안 등 phrasing 문맥에서도 쓰여, 블록이 되면 주변 흐름을 깨기 때문이다.
 */
const WRAP_STYLE: CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  verticalAlign: 'top',
};

/**
 * 제목 행 — **chevron이 왼쪽, 그 옆에 앞 N줄**. 구 안전점검보고서 LegalBasisChip의
 * `flex items-start gap-1`과 같은 배치다(2026-08-10 사용자 요청: "구 화면 느낌으로").
 *
 * 종전에는 텍스트와 토글을 형제로 두었는데, 접힘 텍스트가 `-webkit-box`(블록 성격)라 chevron이
 * 다음 줄로 밀렸다 — 그 제보가 이 구조 변경의 출발이다.
 */
const HEAD_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
};

/** 클램프 대상 텍스트 — 남는 폭을 차지하되 줄어들 수 있어야 chevron이 같은 행에 남는다. */
const TEXT_FLEX: CSSProperties = { flex: '1 1 auto', minWidth: 0 };

/**
 * 펼쳤을 때 head 아래 붙는 본문 — 제목과 시각적으로 구분되도록 살짝 들여쓴다(구 화면의
 * 본문 블록 결). 여기서 잘림은 없다(펼침=전문).
 */
const BODY_STYLE: CSSProperties = {
  display: 'block',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  marginTop: 2,
  paddingLeft: 16,
};

/**
 * 제목 줄을 클릭 대상으로 쓸 때의 커서 — chevron만이 아니라 그 옆 텍스트도 토글 손잡이가 된다
 * (아코디언 헤더 결). 포커스 링·tabIndex는 주지 않는다: 키보드 경로는 chevron 버튼이 이미
 * 갖고 있어(aria-expanded 포함) 두 번째 탭 정지를 만들면 소음이다.
 */
const CLICKABLE_TEXT_STYLE: CSSProperties = { cursor: 'pointer' };

/** chevron 치수 — 구 SI의 `w-3 h-3`(=12px)를 인라인으로 옮긴 값. */
const CHEVRON_STYLE: CSSProperties = { width: 12, height: 12, flexShrink: 0 };

/**
 * 접힘 ▶ / 펼침 ▼ — path d·strokeWidth는 구 안전점검보고서 LegalBasisChip과 동일(heroicons
 * chevron-right / chevron-down). 아이콘 자체는 aria-hidden — 동작 문구는 버튼의 aria-label이 낸다.
 */
function ToggleChevron({ expanded }: { expanded: boolean }): ReactElement {
  return (
    <svg
      style={CHEVRON_STYLE}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d={expanded ? 'M19 9l-7 7-7-7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  );
}

/**
 * 접기 표시 — 대상이 아니면 텍스트를 그대로(래퍼 없이) 반환해 기존 마크업 회귀 0.
 *
 * 클릭/키 이벤트는 **stopPropagation** 한다: 이 컴포넌트는 editEntry:'click'의 표시 상태
 * (role="button" 래퍼) 안에서도 쓰이므로, 전파를 두면 토글 클릭이 편집 진입을 함께 트리거한다.
 * (표시 상태에서 접기 / 편집 진입 시 전문 — 두 축이 충돌하지 않게 하는 접합부.)
 */
export function CollapsibleText({
  text,
  collapseLines,
  toggleOnTextClick = false,
}: {
  text: string;
  collapseLines?: number;
  /**
   * 제목 줄(chevron 옆 텍스트) 클릭으로도 토글할지 — **읽기 전용 표시에서만** 켠다.
   * 클릭이 이미 다른 뜻을 갖는 자리(editEntry:'click' 표시 박스, 목록 항목 = 클릭하면 편집
   * 진입)에서 켜면 그 어포던스를 빼앗는다. 그래서 기본값은 off이고, 소비처가 자기 문맥을 알고
   * 켠다(2026-08-11 사용자 요청: "chevron 뿐 아니라 뒤의 텍스트로도 접었다 폈다").
   */
  toggleOnTextClick?: boolean;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const state = resolveTextCollapse(text, collapseLines);
  if (!state.collapsible) return <>{text}</>;
  const label = collapseToggleLabel(state, expanded);
  // 줄바꿈으로 나눌 수 있으면 제목/본문 2단(구 화면 결), 나눌 수 없는 문단 접힘이면 제자리에서
  // 전문을 편다 — 후자는 "제목"이라 부를 첫 줄이 없어 아래 블록으로 내리면 같은 문단이 두 조각으로
  // 갈라져 보인다.
  const splitBody = state.rest !== '';
  const headText = expanded && !splitBody ? text : state.head;
  // 제목 줄 클릭 토글 — 드래그로 텍스트를 고른 직후의 click은 무시한다(고르자마자 접혀
  // 버리면 선택 자체가 불가능해진다). 펼침 본문에는 걸지 않는다: 길고 읽는 대상이라
  // 손잡이는 제목 줄 하나로 족하다.
  const toggleFromText = toggleOnTextClick
    ? (e: { stopPropagation: () => void }): void => {
        const sel = typeof window === 'undefined' ? null : window.getSelection();
        if (sel && !sel.isCollapsed) return;
        e.stopPropagation();
        setExpanded((v) => !v);
      }
    : undefined;
  return (
    // 래퍼는 <div>가 아니라 <span> — 소비처 중에 읽기전용 표 셀처럼 **<span> 안에서** 이 컴포넌트를
    // 쓰는 곳이 있어(그쪽은 phrasing 문맥) div면 유효하지 않은 중첩이 된다. display는 style이 명시.
    <span style={WRAP_STYLE}>
      <span style={HEAD_ROW_STYLE}>
        <button
          type="button"
          style={TOGGLE_STYLE}
          aria-expanded={expanded}
          aria-label={label}
          title={label}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ToggleChevron expanded={expanded} />
        </button>
        {/* 제목은 **온전히** 보인다 — 줄바꿈으로 나눌 수 있으면(splitBody) 앞 N줄이 곧 제목이므로
            시각 클램프를 걸지 않는다. 클램프를 걸면 그 한 줄이 화면 폭을 넘길 때 `…`로 잘려
            "조문 번호만 보이고 제목이 끊기는" 화면이 된다(2026-08-11 사용자 제보 — 스크린샷의
            `제663조(중량물의 제한) 사업주는 근로자가 …`). 클램프는 줄바꿈이 없어 나눌 수 없는
            문단 접힘에서만 필요하다(그때는 잘림 자체가 "뒤에 더 있다"를 말한다). */}
        <span
          style={{
            ...TEXT_FLEX,
            ...(expanded || splitBody
              ? EXPANDED_TEXT_STYLE
              : collapsedTextStyle(state.visibleLines)),
            ...(toggleFromText ? CLICKABLE_TEXT_STYLE : undefined),
          }}
          onClick={toggleFromText}
        >
          {headText}
        </span>
      </span>
      {/* 펼침 본문 — 감춰졌던 줄만 아래로. 줄 수 안내(+N줄)는 화면에 쓰지 않는다(구 화면은
          chevron만 두고, 그 정보는 토글의 aria-label/title이 낸다). */}
      {expanded && splitBody && <span style={BODY_STYLE}>{state.rest}</span>}
    </span>
  );
}
